import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import BrandLogo from "@/components/brand/BrandLogo";
import { Button, Field, Input, PasswordInput } from "@/components/ui-kit";

const BG = "https://images.unsplash.com/photo-1570793005299-c091be91bbad?w=1200&q=80";

// Só no ambiente local (yarn start). No build de produção (yarn build) NODE_ENV="production" e some.
const SHOW_DEMO = process.env.NODE_ENV === "development";
const DEMO_ACCOUNTS = [
  { label: "Admin", email: "admin@ortopedicasp.com.br", password: "admin123" },
  { label: "Vendedor", email: "vendedor@ortopedicasp.com.br", password: "vendedor123" },
  { label: "Cliente", email: "cliente@teste.com", password: "cliente123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : user.role === "vendedor" ? "/vendedor" : user.role === "balcao" ? "/balcao" : "/");
    } catch (err) { setError(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0B2038]">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        {/* Véu na base. O `opacity-60` sozinho não garante contraste: a parte de
            baixo da foto é clara e a legenda ficava ilegível justamente sobre
            ela — texto branco em cima de cascalho iluminado. */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0B2038] via-[#0B2038]/70 to-transparent" aria-hidden />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-semibold">Ortopédica São Paulo</h2>
          <p className="text-slate-200 mt-2">Plataforma unificada: loja, PDV e gestão.</p>
        </div>
      </div>
      {/* `px-6` no celular (era `p-8`): numa tela de 390px os 32px de cada lado
          espremiam o formulário em 326px. `py-12` mantém o respiro vertical. */}
      <div className="flex items-center justify-center px-6 py-12 sm:p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="inline-flex" data-testid="login-logo-link">
            <BrandLogo size={36} typeHeight={22} />
          </Link>
          {/* A promessa do produto só existia no painel navy, que some abaixo de
              `lg` — no celular a tela abria anônima, um formulário sem contexto. */}
          <p className="lg:hidden text-sm text-slate-500 mt-3">Plataforma unificada: loja, PDV e gestão.</p>

          <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-8">Entrar</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Acesse sua conta de cliente, vendedor ou admin.</p>
          {error && <div role="alert" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="login-error">{error}</div>}
          {/* Sem marcador de obrigatório: num login os dois campos são
              obrigatórios, então o asterisco não distingue nada — só polui. */}
          <div className="space-y-4 mb-6">
            <Field label="E-mail" labelTone="plain">
              {/* `autoCapitalize`/`autoCorrect`/`spellCheck`: no teclado do
                  celular o e-mail sai com a inicial maiúscula e sublinhado de
                  correção, e o erro só aparece depois de enviar. */}
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email" inputMode="email"
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                size="lg" data-testid="login-email-input" />
            </Field>
            <Field label="Senha" labelTone="plain">
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="current-password" size="lg" data-testid="login-password-input" />
            </Field>
          </div>
          <Button type="submit" size="xl" block loading={loading} data-testid="login-submit-btn">
            {loading ? "Entrando..." : "Entrar"}
          </Button>
          <p className="text-sm text-slate-500 mt-4 text-center">
            Não tem conta? <Link to="/cadastro" data-testid="login-register-link" className="text-brand-600 font-medium hover:underline">Cadastre-se</Link>
          </p>
          {SHOW_DEMO && (
            <div className="mt-8 border-t border-slate-100 pt-4" data-testid="login-demo-accounts">
              <p className="text-xs font-medium text-slate-400 mb-2 text-center">Contas de teste (ambiente local)</p>
              <div className="space-y-1.5">
                {DEMO_ACCOUNTS.map((acc) => (
                  <button key={acc.email} type="button"
                    onClick={() => { setEmail(acc.email); setPassword(acc.password); }}
                    className="w-full flex items-center justify-between gap-2 text-xs px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:border-brand-500 hover:bg-slate-50 transition-colors">
                    <span className="font-medium text-slate-700">{acc.label}</span>
                    <span className="text-slate-400 truncate">{acc.email} · {acc.password}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
